import React, { useCallback, useEffect, useState } from "react";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import BoxHeader from "../../Components/Box/BoxHeader";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Box,
  FormLabel,
  Grid,
} from "@mui/material";
import { CustomTablePagination } from "../../Components/Pagination/CustomTablePagination";
import TableSearch from "../../Components/Search/TableSearch";
import { useDispatch } from "react-redux";
import TagIcon from "../../Assets/images/menu-icons/assignment_selected.png";
import { AntTab, AntTabs } from "../../Components/Tab/AntTab";
import { PermissionCheck } from "../../App/PermissionCheck";
import { GetApiMethod, PostApiMethod, PutApiMethod, DeleteApiMethod } from "../../Utils/ApiService";
import { showLoader } from "../../Store/loaderSlice";
import { showToaster } from "../../Store/toastSlice";
import { CustomModal, ModalStyle } from "../../Components/ModalStyle";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import MenuGroup from "../../Components/DropDown/MenuGroup";

interface TagDto {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

interface TagListDto {
  rows: TagDto[];
  totalCount: number;
}

const TagManagement = () => {
  const [tagList, setTagList] = useState<TagListDto>({ rows: [], totalCount: 0 });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [formModal, setFormModal] = useState(false);
  const [editTag, setEditTag] = useState<TagDto | null>(null);
  const dispatch = useDispatch();

  // Fetch tags with pagination and search
  const getTags = useCallback(async () => {
    try {
      dispatch(showLoader({ showLoader: true }));
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        search: searchTerm.trim(),
        sort: "createdAt",
        sortDirection: "DESC",
      });
      const res = await GetApiMethod(`tags?${params.toString()}`);
      const responseData = res.data.data || res.data;

      setTagList({
        rows: responseData.rows || [],
        totalCount: responseData.count || 0,
      });
    } catch (err) {
      dispatch(showToaster({
        dialogBgColor: "bg-danger",
        dialogMsg: "Failed to fetch tags",
        showDialog: true,
        timer: 3000,
      }));
    } finally {
      dispatch(showLoader({ showLoader: false }));
    }
  }, [dispatch, page, rowsPerPage, searchTerm]);

  // Save or update tag
  const handleSaveTag = async (name: string, id?: string) => {
    try {
      const isDuplicate = tagList.rows.some(
        tag => tag.name.toLowerCase() === name.toLowerCase() && tag.id !== id
      );
      if (isDuplicate) {
        dispatch(showToaster({
          dialogBgColor: "bg-warning",
          dialogMsg: `Tag "${name}" already exists`,
          showDialog: true,
          timer: 3000,
        }));
        return;
      }

      dispatch(showLoader({ showLoader: true }));

      if (id) {
        await PutApiMethod(`tags/${id}`, { name });
        dispatch(showToaster({
          dialogBgColor: "bg-success",
          dialogMsg: `Tag "${name}" updated successfully`,
          showDialog: true,
          timer: 3000,
        }));
      } else {
        await PostApiMethod("tags", { name });
        dispatch(showToaster({
          dialogBgColor: "bg-success",
          dialogMsg: `Tag "${name}" created successfully`,
          showDialog: true,
          timer: 3000,
        }));
      }

      resetForm();
      await getTags();
    } catch (err: any) {
      console.error("Error saving tag:", err);
      dispatch(showToaster({
        dialogBgColor: "bg-danger",
        dialogMsg: err?.response?.data?.message || `Failed to ${id ? "update" : "create"} tag`,
        showDialog: true,
        timer: 3000,
      }));
    } finally {
      dispatch(showLoader({ showLoader: false }));
    }
  };

  const resetForm = useCallback(() => {
    setFormModal(false);
    setEditTag(null);
  }, []);

  // Delete tag
  const handleDeleteTag = async (id: string, name?: string) => {
    try {
      dispatch(showLoader({ showLoader: true }));
      await DeleteApiMethod(`tags/${id}`);
      await getTags();
      dispatch(showToaster({
        dialogBgColor: "bg-success",
        dialogMsg: `Tag "${name || ''}" deleted successfully`,
        showDialog: true,
        timer: 3000,
      }));
    } catch (err: any) {
      console.error("Error deleting tag:", err);
      dispatch(showToaster({
        dialogBgColor: "bg-danger",
        dialogMsg: err?.response?.data?.message || "Failed to delete tag",
        showDialog: true,
        timer: 3000,
      }));
    } finally {
      dispatch(showLoader({ showLoader: false }));
    }
  };

  useEffect(() => {
    getTags();
  }, [getTags]);

  return (
    <AuthorizedLayout>
      <BoxHeader
        dashboard_name="Tag Management"
        button_name="Add Tag"
        image={TagIcon}
        onAdd={() => { setFormModal(true); setEditTag(null); }}
        button_show={PermissionCheck("manage_tags")}
      >
        <AntTabs value={0}>
          <AntTab label="All Tags" className="tab-menu" />
        </AntTabs>
      </BoxHeader>

      <TableSearch
        value={searchTerm}
        onChange={(value) => { setSearchTerm(value); setPage(0); }}
        placeholder="Search tags by name..."
      />

      <TableContainer className="mt-2">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tagList.rows.map((tag, key) => (
              <TableRow key={key}>
                <TableCell>{tag.name}</TableCell>
                <TableCell align="right">
                  <MenuGroup
                    id={tag.id}
                    view={false}
                    edit={true}
                    delete={true}
                    clickEdit={() => { setEditTag(tag); setFormModal(true); }}
                    clickDelete={() => handleDeleteTag(tag.id, tag.name)}
                  />
                </TableCell>
              </TableRow>
            ))}
            {tagList.rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={2} align="center">No Data</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomTablePagination
        count={tagList.totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />

      <CustomModal
        open={formModal}
        onClose={() => { resetForm(); dispatch(showLoader({ showLoader: false })); }}
        header={`${editTag?.id ? "Edit" : "Create"} Tag`}
        style={ModalStyle({ width: 600, height: "auto" })}
      >
        <Formik
          key={editTag?.id || 'new'}
          initialValues={{ name: editTag?.name || "" }}
          validationSchema={Yup.object({
            name: Yup.string()
              .trim()
              .required("Tag name is required")
              .min(2, "Tag name must be at least 2 characters"),
          })}
          onSubmit={async (values) => { await handleSaveTag(values.name.trim(), editTag?.id); }}
          enableReinitialize={true}
        >
          {({ handleSubmit, handleChange, values, errors, touched }) => (
            <Form onSubmit={handleSubmit}>
              <Box className="modal-body">
                <Grid item md={12} m={2}>
                  <Grid container spacing={1} className="p-3">
                    <FormLabel className="fw-bold">
                      Tag Name:<sup className="text-danger">*</sup>
                    </FormLabel>
                    <TextField
                      className="w-100"
                      placeholder="Enter tag name..."
                      variant="standard"
                      value={values.name}
                      name="name"
                      onChange={handleChange}
                    />
                    {touched.name && errors.name && <div className="text-danger">{errors.name}</div>}
                  </Grid>
                </Grid>
                <Box className="d-flex justify-content-end mt-3 p-3">
                  <button className="btn btn-danger" type="submit">Save</button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </CustomModal>
    </AuthorizedLayout>
  );
};

export default TagManagement;
